import React, { Fragment, useEffect, useState } from 'react'
import { superAxiosRequest } from 'axiosApi'
import { splitNumberDigits } from 'helpers/math'
import useTableFixedHeader from 'hooks/useTableFixedHeader'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import THead from 'components/ContentBox/Global/ReportTable/THead'
import Loader from 'components/ContentBox/Global/Loader/Loader'
import st from 'components/ContentBox/Global/ReportTable/Table.module.scss'
import { ExcelRowsParams, setCellParams } from 'components/utils/ExcelExport'
import ExportExcelButton from '../ExportExcelButton/ExportExcelButton'
import { observer } from 'mobx-react'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import { useHistory } from 'react-router-dom'

// Функция для деления на 100 с форматированием
const divideBy100 = (value) => {
  const num = Number(value)
  if (isNaN(num)) return 0
  return Math.round(num / 100)
}

// Функция для расчета студентов на начало периода
const calculateStudentsStartPeriod = (studentData) => {
  return (
    (studentData?.active_student || 0) +
    (studentData?.left_student || 0) -
    (studentData?.new_student || 0)
  )
}

// Функция для форматирования числа с процентом
const formatNumberWithPercent = (value, baseValue) => {
  const num = Number(value)
  const base = Number(baseValue)
  if (base === 0) return splitNumberDigits(num)
  const percent = (num / base) * 100
  return `${splitNumberDigits(num)} (${percent.toFixed(1)}%)`
}

// Функция для форматирования целых чисел
const formatInteger = (value) => {
  const num = Number(value)
  if (isNaN(num)) return '0'
  return splitNumberDigits(Math.round(num))
}

// Функция для форматирования целых чисел с делением на 100
const formatIntegerDivided = (value) => {
  return formatInteger(divideBy100(value))
}

// Функция для расчета среднего значения (исключая нулевые значения)
const calculateAverage = (values) => {
  const validValues = values.filter(
    (v) => v !== null && v !== undefined && !isNaN(v) && v !== 0,
  )
  if (validValues.length === 0) return 0
  return validValues.reduce((a, b) => a + b, 0) / validValues.length
}

// Функция для расчета наполняемости студентов (часы студентов / часы учителей)
const calculateCapacity = (studentHours, teacherHours) => {
  const sHours = Number(studentHours) || 0
  const tHours = Number(teacherHours) || 0
  if (tHours === 0) return '0.00'
  return (sHours / tHours).toFixed(2)
}

// Функция для расчета дохода с 1 ак часа (Доход по часам / Часы студентов)
const calculateIncomePerHour = (incomeByHoursWorked, hoursWorked) => {
  const income = Number(incomeByHoursWorked) || 0
  const hours = Number(hoursWorked) || 0
  if (hours === 0) return 0
  return income / hours
}

// Функция для расчета дохода с 1 ак часа по наполняемости (Доход с 1 ак часа * Наполняемость)
const calculateIncomePerHourByCapacity = (incomePerHour, capacity) => {
  const income = Number(incomePerHour) || 0
  const cap = Number(capacity) || 0
  return income * cap
}

const ManagementReport = () => {
  const [catalog, setCatalog] = useState({ locations: [], program_types: [] })
  const [filter, updateFilter] = useLocalStorageFilter()
  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore
  const history = useHistory()

  const {
    refWrapper,
    refSticky,
    refThead,
    data,
    setData,
    isLoading,
    setIsLoading,
    getTdClassNames,
  } = useTableFixedHeader()

  const { translate } = useLocalization({
    section: 'ebooks',
    subsection: 'ebooks_books',
    dict: {
      edit: 'Edit',
      copy: 'Copy',
      delete: 'Delete',
      save: 'Save',
      filter: 'Filter',
      company: 'Company',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  // Преобразование данных из формата бэка в формат для таблицы
  const transformData = (responseData) => {
    if (!responseData) return { locations: [] }

    const locationsArray = []
    const programTypesMap = new Map()
    let total = {
      active_students: 0,
      new_students: 0,
      left_students: 0,
      students_start_period: 0,
      students_end_period: 0,
      hours_worked: 0,
      avg_students_check: 0,
      income_by_hours_worked: 0,
      expense_by_hours_worked: 0,
      profit_by_school: 0,
      income_per_hour: 0,
      income_per_hour_ach: 0,
      expense_per_hour: 0,
      profit_per_hour: 0,
      profit_per_sqm: 0,
      profit_per_classroom: 0,
      teachers_hours_worked: 0,
      income_per_hour_values: [],
      expense_per_hour_values: [],
      profit_per_hour_values: [],
      profit_per_sqm_totals: [],
      profit_per_classroom_totals: [],
    }

    Object.keys(responseData).forEach((locationId) => {
      const locationData = responseData[locationId]
      const locationName = locationData.location_name
      delete locationData.location_name

      const programTypes = []
      let locationTotal = {
        active_students: 0,
        new_students: 0,
        left_students: 0,
        students_start_period: 0,
        students_end_period: 0,
        hours_worked: 0,
        avg_students_check: 0,
        income_by_hours_worked: 0,
        expense_by_hours_worked: 0,
        profit_by_school: 0,
        income_per_hour: 0,
        income_per_hour_ach: 0,
        expense_per_hour: 0,
        profit_per_hour: 0,
        profit_per_sqm: 0,
        profit_per_classroom: 0,
        teachers_hours_worked: 0,
        income_per_hour_values: [],
        expense_per_hour_values: [],
        profit_per_hour_values: [],
      }

      Object.keys(locationData).forEach((programTypeId) => {
        const programData = locationData[programTypeId]

        // Сохраняем типы программ для фильтра
        if (!programTypesMap.has(programTypeId)) {
          programTypesMap.set(programTypeId, {
            id: parseInt(programTypeId),
            name: programData.program_type_name,
          })
        }

        // Получаем данные из бэка
        const activeStudents = programData.active_students || 0
        const newStudents = programData.new_students || 0
        const leftStudents = programData.left_students || 0
        const teachersHoursWorked = programData.teachers_hours_worked || 0

        // Студенты на начало периода = активные + выбывшие - новые
        const studentsStartPeriod = activeStudents + leftStudents - newStudents

        const incomePerHour = programData.income_per_hour || 0
        const incomePerHourAch = programData.income_per_hour_ach || 0
        const expensePerHour = programData.expense_per_hour || 0
        const profitPerHour = programData.profit_per_hour || 0
        const profitPerSqm = programData.profit_per_sqm || 0
        const profitPerClassroom = programData.profit_per_classroom || 0

        programTypes.push({
          program_type: parseInt(programTypeId),
          program_type_name: programData.program_type_name,
          active_students: activeStudents,
          new_students: newStudents,
          left_students: leftStudents,
          students_start_period: studentsStartPeriod,
          students_end_period: activeStudents,
          hours_worked: programData.hours_worked || 0,
          avg_students_check: programData.avg_students_check || 0,
          income_by_hours_worked: programData.income_by_hours_worked || 0,
          expense_by_hours_worked: programData.expense_by_hours_worked || 0,
          profit_by_school: programData.profit_by_school || 0,
          income_per_hour: incomePerHour,
          income_per_hour_ach: incomePerHourAch,
          expense_per_hour: expensePerHour,
          profit_per_hour: profitPerHour,
          profit_per_sqm: profitPerSqm,
          profit_per_classroom: profitPerClassroom,
          teachers_hours_worked: teachersHoursWorked,
        })

        // Суммируем итоги по локации
        locationTotal.active_students += activeStudents
        locationTotal.new_students += newStudents
        locationTotal.left_students += leftStudents
        locationTotal.students_start_period += studentsStartPeriod
        locationTotal.students_end_period += activeStudents
        locationTotal.hours_worked += programData.hours_worked || 0
        locationTotal.teachers_hours_worked += teachersHoursWorked
        locationTotal.income_by_hours_worked +=
          programData.income_by_hours_worked || 0
        locationTotal.expense_by_hours_worked +=
          programData.expense_by_hours_worked || 0
        locationTotal.profit_by_school += programData.profit_by_school || 0
        locationTotal.profit_per_sqm += profitPerSqm
        locationTotal.profit_per_classroom += profitPerClassroom

        // Сохраняем значения для среднего (включая нулевые, они будут отфильтрованы позже)
        locationTotal.income_per_hour_values.push(incomePerHour)
        locationTotal.expense_per_hour_values.push(expensePerHour)
        locationTotal.profit_per_hour_values.push(profitPerHour)

        // Суммируем общие итоги
        total.active_students += activeStudents
        total.new_students += newStudents
        total.left_students += leftStudents
        total.students_start_period += studentsStartPeriod
        total.students_end_period += activeStudents
        total.hours_worked += programData.hours_worked || 0
        total.teachers_hours_worked += teachersHoursWorked
        total.income_by_hours_worked += programData.income_by_hours_worked || 0
        total.expense_by_hours_worked +=
          programData.expense_by_hours_worked || 0
        total.profit_by_school += programData.profit_by_school || 0
        total.profit_per_sqm += profitPerSqm
        total.profit_per_classroom += profitPerClassroom

        // Сохраняем значения для среднего (включая нулевые, они будут отфильтрованы позже)
        total.income_per_hour_values.push(incomePerHour)
        total.expense_per_hour_values.push(expensePerHour)
        total.profit_per_hour_values.push(profitPerHour)
      })

      // Средний чек по локации (если есть студенты)
      if (locationTotal.active_students > 0) {
        locationTotal.avg_students_check =
          locationTotal.income_by_hours_worked / locationTotal.active_students
      }

      // Рассчитываем средние значения для локации (исключая нулевые)
      locationTotal.expense_per_hour = calculateAverage(
        locationTotal.expense_per_hour_values,
      )

      // НОВОЕ: Расчет income_per_hour_ach для ИТОГО по локации (Доход по часам / Часы студентов)
      locationTotal.income_per_hour_ach = calculateIncomePerHour(
        locationTotal.income_by_hours_worked,
        locationTotal.hours_worked,
      )

      // НОВОЕ: Расчет income_per_hour для ИТОГО по локации (Доход с 1 ак часа * Наполняемость)
      const locationCapacity = calculateCapacity(
        locationTotal.hours_worked,
        locationTotal.teachers_hours_worked,
      )
      locationTotal.income_per_hour = calculateIncomePerHourByCapacity(
        locationTotal.income_per_hour_ach,
        locationCapacity,
      )

      // ИСПРАВЛЕНО: Грязная прибыль по школе с 1 ак часа = Доход с 1 ак часа по наполняемости - Расход с 1 ак часа (ЗП учителя)
      locationTotal.profit_per_hour =
        locationTotal.income_per_hour - locationTotal.expense_per_hour

      // СОХРАНЯЕМ ЗНАЧЕНИЯ ИТОГО ПО ЛОКАЦИИ ДЛЯ РАСЧЕТА СРЕДНЕГО В ВСЕГО ИТОГО
      total.profit_per_sqm_totals.push(locationTotal.profit_per_sqm)
      total.profit_per_classroom_totals.push(locationTotal.profit_per_classroom)

      locationsArray.push({
        id: parseInt(locationId),
        name: locationName,
        program_types: programTypes,
        total: locationTotal,
      })
    })

    // Общие итоги
    if (total.active_students > 0) {
      total.avg_students_check =
        total.income_by_hours_worked / total.active_students
    }

    // Рассчитываем средние значения для общего итога (исключая нулевые)
    total.expense_per_hour = calculateAverage(total.expense_per_hour_values)

    // НОВОЕ: Расчет income_per_hour_ach для ВСЕГО ИТОГО (Доход по часам / Часы студентов)
    total.income_per_hour_ach = calculateIncomePerHour(
      total.income_by_hours_worked,
      total.hours_worked,
    )

    // НОВОЕ: Расчет income_per_hour для ВСЕГО ИТОГО (Доход с 1 ак часа * Наполняемость)
    const totalCapacity = calculateCapacity(
      total.hours_worked,
      total.teachers_hours_worked,
    )
    total.income_per_hour = calculateIncomePerHourByCapacity(
      total.income_per_hour_ach,
      totalCapacity,
    )

    // ИСПРАВЛЕНО: Грязная прибыль по школе с 1 ак часа = Доход с 1 ак часа по наполняемости - Расход с 1 ак часа (ЗП учителя)
    total.profit_per_hour = total.income_per_hour - total.expense_per_hour

    // ВАЖНО: для profit_per_sqm и profit_per_classroom в ВСЕГО ИТОГО
    // используем среднее арифметическое от значений "Итого" по каждой локации
    total.profit_per_sqm = calculateAverage(total.profit_per_sqm_totals)
    total.profit_per_classroom = calculateAverage(
      total.profit_per_classroom_totals,
    )

    return {
      locations: locationsArray,
      total: total,
    }
  }

  const filterCnf = [
    {
      type: 'select-multi',
      label: 'Локация',
      nameState: 'location',
      placeholder: 'Локация',
      apiLink: 'db/locations/?limit=99',
      sort_key: 'name',
    },
    {
      type: 'date_day_picker',
      nameState: ['date_from', 'date_until'],
      label: 'Период',
      isRequired: true,
    },
    {
      type: 'select-multi',
      label: 'Тип программы',
      placeholder: 'Тип программы',
      apiLink: 'db/programtypes/?limit=99',
      sort_key: 'name',
      nameState: 'program_type',
      is_search: true,
    },
    {
      type: 'type_selector',
      label: 'Формат',
      values: [
        { id: 1, name: 'Онлайн' },
        { id: 0, name: 'Офлайн' },
      ],
      nameState: 'online',
    },
  ]

  const calculateGrossProfitPercentage = (
    profitBySchool,
    incomeByHoursWorked,
  ) => {
    if (!incomeByHoursWorked || incomeByHoursWorked === 0) return '0.00'
    const percentage = (profitBySchool / incomeByHoursWorked) * 100
    return percentage.toFixed(2)
  }

  const calculateProfitabilityPercentage = (
    programProfit,
    locationTotalProfit,
  ) => {
    if (!locationTotalProfit || locationTotalProfit === 0) return '0.00'
    const percentage = (programProfit / locationTotalProfit) * 100
    return percentage.toFixed(2)
  }

  const cfg = {
    cols: [
      {
        name: 'Школа',
        group: true,
        formatter: (row) =>
          catalog.locations?.find((l) => l.id === row.id)?.name ||
          row.name ||
          row.id,
        classes: [st.color, st.fw500],
        align: 'left',
      },
      {
        name: 'Тип программы',
        formatter: (row) => row.program_type_name,
        classes: [st.color, st.fz12, st.fw500],
        align: 'left',
      },
      {
        name: 'Студенты на начало периода',
        formatter: (row) => splitNumberDigits(row.students_start_period || 0),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Новые студенты',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.new_students || 0,
            studentsStartPeriod || row.students_start_period || 0,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Выбывшие студенты',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.left_students || 0,
            studentsStartPeriod || row.students_start_period || 0,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Студенты на конец периода',
        formatter: (row, studentsStartPeriod) => {
          return formatNumberWithPercent(
            row.active_students || 0,
            studentsStartPeriod || row.students_start_period || 0,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Наполняемость студентов по отработанным часам',
        formatter: (row) =>
          calculateCapacity(row.hours_worked, row.teachers_hours_worked),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Доход по этим отработанным часам',
        formatter: (row) =>
          formatInteger(divideBy100(row.income_by_hours_worked)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Расход по отработанным часа (ЗП учителя)',
        formatter: (row) =>
          formatInteger(divideBy100(row.expense_by_hours_worked)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Грязная прибыль по школе',
        formatter: (row) => formatInteger(divideBy100(row.profit_by_school)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Грязная прибыль по школе в % ',
        formatter: (row, locationTotalProfit) => {
          return calculateProfitabilityPercentage(
            row.profit_by_school || 0,
            locationTotalProfit || 0,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Рентабельность, %',
        formatter: (row) => {
          return calculateGrossProfitPercentage(
            row.profit_by_school || 0,
            row.income_by_hours_worked || 0,
          )
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Количество отработанных часов, ак. ч. студентами',
        formatter: (row) =>
          splitNumberDigits(row.hours_worked?.toFixed(2) || '0.00'),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Средний чек студента',
        formatter: (row) => formatInteger(divideBy100(row.avg_students_check)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Доход с 1 ак часа',
        formatter: (row) => {
          return formatInteger(divideBy100(row.income_per_hour_ach))
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Доход с 1 ак часа по наполняемости',
        formatter: (row) => {
          return formatInteger(divideBy100(row.income_per_hour))
        },
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Расход с 1 ак часа (ЗП учителя)',
        formatter: (row) => formatInteger(divideBy100(row.expense_per_hour)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Грязная прибыль по школе с 1 ак часа',
        formatter: (row) => formatInteger(divideBy100(row.profit_per_hour)),
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Грязная прибыль с 1 кв м',
        formatter: () => '', // Пустая ячейка в основной таблице
        classes: [st.fz12],
        align: 'left',
      },
      {
        name: 'Грязная прибыль с аудитории',
        formatter: () => '', // Пустая ячейка в основной таблице
        classes: [st.fz12],
        align: 'left',
      },
    ],
  }

  // Получение каталога (список школ и типов программ) - без запроса данных
  useEffect(() => {
    setCatalog({
      locations: [],
      program_types: [],
    })
    setIsLoading(false)
  }, [])

  // Получение данных с фильтрами - только при наличии фильтров
  useEffect(() => {
    if (!filter || Object.keys(filter).length === 0) {
      setData(null)
      return
    }

    if (!filter.date_from || !filter.date_until) {
      return
    }

    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'progress_report/management_report',
      params: {
        ...filter,
      },
    })
      .then(({ data }) => {
        let filteredData = data

        if (filter.location && filter.location.length > 0) {
          const filteredObj = {}
          filter.location.forEach((locId) => {
            if (data[locId]) {
              filteredObj[locId] = data[locId]
            }
          })
          filteredData = filteredObj
        }

        if (filter.program_type && filter.program_type.length > 0) {
          Object.keys(filteredData).forEach((locationId) => {
            const locationData = filteredData[locationId]
            const filteredPrograms = {}

            filter.program_type.forEach((progId) => {
              if (locationData[progId]) {
                filteredPrograms[progId] = locationData[progId]
              }
            })

            filteredData[locationId] = {
              ...filteredPrograms,
              location_name: locationData.location_name,
            }
          })
        }

        const locationsList = []
        const programTypesSet = new Map()

        Object.keys(filteredData).forEach((locationId) => {
          const locationData = filteredData[locationId]
          locationsList.push({
            id: parseInt(locationId),
            name: locationData.location_name,
          })

          delete locationData.location_name

          Object.keys(locationData).forEach((programTypeId) => {
            const programData = locationData[programTypeId]
            if (!programTypesSet.has(programTypeId)) {
              programTypesSet.set(programTypeId, {
                id: parseInt(programTypeId),
                name: programData.program_type_name,
              })
            }
          })
        })

        setCatalog({
          locations: locationsList,
          program_types: Array.from(programTypesSet.values()),
        })

        const transformedData = transformData(filteredData)
        setData(transformedData)
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }, [filter])

  useEffect(() => {
    return AsideFilterStore.unmountFilter
  }, [])

  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname !== '/reports/management-report') {
        updateFilter({})
      }
    })
    return () => {
      unlisten()
    }
  }, [history, updateFilter])

  // Функция для получения текста фильтров
  const getFilterText = () => {
    const parts = []

    if (filter.location && filter.location.length > 0) {
      const locationNames = filter.location
        .map((id) => {
          const loc = catalog.locations?.find((l) => l.id === parseInt(id))
          return loc?.name || id
        })
        .join(', ')
      parts.push(`Локация - ${locationNames}`)
    }

    if (filter.date_from) {
      parts.push(`Дата начала - ${filter.date_from}`)
    }

    if (filter.date_until) {
      parts.push(`Дата окончания - ${filter.date_until}`)
    }

    if (filter.program_type && filter.program_type.length > 0) {
      const programNames = filter.program_type
        .map((id) => {
          const pt = catalog.program_types?.find((p) => p.id === parseInt(id))
          return pt?.name || id
        })
        .join(', ')
      parts.push(`Тип программы - ${programNames}`)
    }

    if (
      filter.online !== undefined &&
      filter.online !== null &&
      filter.online !== ''
    ) {
      const onlineMap = {
        0: 'Офлайн',
        1: 'Онлайн',
        2: 'Все группы',
      }
      parts.push(`Формат - ${onlineMap[filter.online] || filter.online}`)
    }

    if (filter.search) {
      parts.push(`Поиск - ${filter.search}`)
    }

    return parts.join('; ')
  }

  const downloadExcel = (exportData) => {
    import('xlsx-js-style').then((XLSX) => {
      const reportTitle = 'Рентабельность программ'
      const filterText = getFilterText()

      // Создаем заголовки с правильным форматированием
      const headRows = [
        // Первая строка - название отчета (объединенная ячейка)
        [
          {
            v: reportTitle,
            s: {
              font: { bold: true, sz: 18, color: { rgb: '1A3C5E' } },
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          },
        ],
        // Вторая строка - фильтры
        [
          {
            v: filterText || 'Фильтры не выбраны',
            s: {
              font: { sz: 11, italic: true, color: { rgb: '333333' } },
              alignment: { horizontal: 'left', vertical: 'center' },
            },
          },
        ],
        // Пустая строка
        [],
        // Заголовки колонок
        setCellParams(
          cfg.cols.map((col) => ({ v: col.name })),
          ExcelRowsParams.headerRowParam,
        ),
      ]

      let tableData = []
      let merges = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cfg.cols.length - 1 } }]
      let wscols = cfg.cols.map(() => ({ wch: 22 }))
      let rows = [{ hpx: 50 }, { hpx: 35 }, { hpx: 15 }]
      let currentRow = 4

      // Функция для форматирования в Excel
      const formatExcelNumberWithPercent = (value, baseValue) => {
        const num = Number(value)
        const base = Number(baseValue)
        if (base === 0) return splitNumberDigits(num)
        const percent = (num / base) * 100
        return `${splitNumberDigits(num)} (${percent.toFixed(1)}%)`
      }

      // Функция для форматирования целых чисел в Excel
      const formatExcelInteger = (value) => {
        const num = Number(value)
        if (isNaN(num)) return '0'
        return splitNumberDigits(Math.round(num))
      }

      // Функция для форматирования целых чисел с делением на 100 в Excel
      const formatExcelIntegerDivided = (value) => {
        return formatExcelInteger(divideBy100(value))
      }

      // ВСЕГО ИТОГО - всегда показываем, независимо от выбранных локаций
      if (exportData?.total) {
        const totalStartPeriod = exportData.total.students_start_period || 0

        const totalRow = [
          { v: 'ВСЕГО ИТОГО', ...ExcelRowsParams.footerRowParams.firstCell }, // 1
          { v: '' }, // 2
          { v: splitNumberDigits(totalStartPeriod) }, // 3
          {
            v: formatExcelNumberWithPercent(
              exportData.total.new_students || 0,
              totalStartPeriod,
            ),
          }, // 4
          {
            v: formatExcelNumberWithPercent(
              exportData.total.left_students || 0,
              totalStartPeriod,
            ),
          }, // 5
          {
            v: formatExcelNumberWithPercent(
              exportData.total.active_students || 0,
              totalStartPeriod,
            ),
          }, // 6
          {
            v: calculateCapacity(
              exportData.total.hours_worked,
              exportData.total.teachers_hours_worked,
            ),
          }, // 7
          {
            v: formatExcelInteger(
              divideBy100(exportData.total.income_by_hours_worked),
            ),
          }, // 8
          {
            v: formatExcelInteger(
              divideBy100(exportData.total.expense_by_hours_worked),
            ),
          }, // 9
          {
            v: formatExcelInteger(
              divideBy100(exportData.total.profit_by_school),
            ),
          }, // 10
          {
            v: calculateProfitabilityPercentage(
              exportData.total.profit_by_school || 0,
              exportData.total.profit_by_school || 0,
            ),
          }, // 11
          {
            v: calculateGrossProfitPercentage(
              exportData.total.profit_by_school || 0,
              exportData.total.income_by_hours_worked || 0,
            ),
          }, // 12
          {
            v: splitNumberDigits(
              exportData.total.hours_worked?.toFixed(2) || '0.00',
            ),
          }, // 13
          {
            v: formatExcelInteger(
              divideBy100(exportData.total.avg_students_check),
            ),
          }, // 14
          {
            v: formatExcelIntegerDivided(exportData.total.income_per_hour_ach),
          }, // 15 - Доход с 1 ак часа
          { v: formatExcelIntegerDivided(exportData.total.income_per_hour) }, // 16 - Доход с 1 ак часа по наполняемости
          { v: formatExcelIntegerDivided(exportData.total.expense_per_hour) }, // 17
          { v: formatExcelIntegerDivided(exportData.total.profit_per_hour) }, // 18
          { v: formatExcelIntegerDivided(exportData.total.profit_per_sqm) }, // 19
          {
            v: formatExcelIntegerDivided(exportData.total.profit_per_classroom),
          }, // 20
        ]

        const formattedTotalRow = totalRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(formattedTotalRow)
        currentRow++
      }

      exportData?.locations?.forEach((location) => {
        const locationTotalProfitBySchool = location.program_types.reduce(
          (sum, program) => sum + (program.profit_by_school || 0),
          0,
        )

        if (location.program_types.length > 1) {
          merges.push({
            s: { r: currentRow, c: 0 },
            e: { r: currentRow + location.program_types.length - 1, c: 0 },
          })
        }

        location.program_types.forEach((program_type) => {
          const startPeriod = program_type.students_start_period || 0
          let rowData = [
            {
              v:
                catalog.locations?.find((el) => el.id === location.id)?.name ||
                location.id,
            }, // 1
            { v: program_type.program_type_name }, // 2
            { v: splitNumberDigits(startPeriod) }, // 3
            {
              v: formatExcelNumberWithPercent(
                program_type.new_students || 0,
                startPeriod,
              ),
            }, // 4
            {
              v: formatExcelNumberWithPercent(
                program_type.left_students || 0,
                startPeriod,
              ),
            }, // 5
            {
              v: formatExcelNumberWithPercent(
                program_type.active_students || 0,
                startPeriod,
              ),
            }, // 6
            {
              v: calculateCapacity(
                program_type.hours_worked,
                program_type.teachers_hours_worked,
              ),
            }, // 7
            {
              v: formatExcelInteger(
                divideBy100(program_type.income_by_hours_worked),
              ),
            }, // 8
            {
              v: formatExcelInteger(
                divideBy100(program_type.expense_by_hours_worked),
              ),
            }, // 9
            {
              v: formatExcelInteger(divideBy100(program_type.profit_by_school)),
            }, // 10
            {
              v: calculateProfitabilityPercentage(
                program_type.profit_by_school || 0,
                locationTotalProfitBySchool,
              ),
            }, // 11
            {
              v: calculateGrossProfitPercentage(
                program_type.profit_by_school || 0,
                program_type.income_by_hours_worked || 0,
              ),
            }, // 12
            {
              v: splitNumberDigits(
                program_type.hours_worked?.toFixed(2) || '0.00',
              ),
            }, // 13
            {
              v: formatExcelInteger(
                divideBy100(program_type.avg_students_check),
              ),
            }, // 14
            {
              v: formatExcelInteger(
                divideBy100(program_type.income_per_hour_ach),
              ),
            }, // 15 - Доход с 1 ак часа
            {
              v: formatExcelInteger(divideBy100(program_type.income_per_hour)),
            }, // 16 - Доход с 1 ак часа по наполняемости
            {
              v: formatExcelInteger(divideBy100(program_type.expense_per_hour)),
            }, // 17
            {
              v: formatExcelInteger(divideBy100(program_type.profit_per_hour)),
            }, // 18
            { v: '' }, // 19
            { v: '' }, // 20
          ]
          rowData = setCellParams(rowData, ExcelRowsParams.tableRowParam.cell)
          tableData.push(rowData)
          currentRow++
        })

        const locationStartPeriod = location.total?.students_start_period || 0
        const footerRow = [
          { v: 'Итого', ...ExcelRowsParams.footerRowParams.firstCell }, // 1
          { v: '' }, // 2
          { v: splitNumberDigits(locationStartPeriod) }, // 3
          {
            v: formatExcelNumberWithPercent(
              location.total?.new_students || 0,
              locationStartPeriod,
            ),
          }, // 4
          {
            v: formatExcelNumberWithPercent(
              location.total?.left_students || 0,
              locationStartPeriod,
            ),
          }, // 5
          {
            v: formatExcelNumberWithPercent(
              location.total?.active_students || 0,
              locationStartPeriod,
            ),
          }, // 6
          {
            v: calculateCapacity(
              location.total?.hours_worked,
              location.total?.teachers_hours_worked,
            ),
          }, // 7
          {
            v: formatExcelInteger(
              divideBy100(location.total?.income_by_hours_worked),
            ),
          }, // 8
          {
            v: formatExcelInteger(
              divideBy100(location.total?.expense_by_hours_worked),
            ),
          }, // 9
          {
            v: formatExcelInteger(
              divideBy100(location.total?.profit_by_school),
            ),
          }, // 10
          {
            v: calculateProfitabilityPercentage(
              location.total?.profit_by_school || 0,
              location.total?.profit_by_school || 0,
            ),
          }, // 11
          {
            v: calculateGrossProfitPercentage(
              location.total?.profit_by_school || 0,
              location.total?.income_by_hours_worked || 0,
            ),
          }, // 12
          {
            v: splitNumberDigits(
              location.total?.hours_worked?.toFixed(2) || '0.00',
            ),
          }, // 13
          {
            v: formatExcelInteger(
              divideBy100(location.total?.avg_students_check),
            ),
          }, // 14
          { v: formatExcelIntegerDivided(location.total?.income_per_hour_ach) }, // 15 - Доход с 1 ак часа
          { v: formatExcelIntegerDivided(location.total?.income_per_hour) }, // 16 - Доход с 1 ак часа по наполняемости
          { v: formatExcelIntegerDivided(location.total?.expense_per_hour) }, // 17
          { v: formatExcelIntegerDivided(location.total?.profit_per_hour) }, // 18
          { v: formatExcelIntegerDivided(location.total?.profit_per_sqm) }, // 19
          {
            v: formatExcelIntegerDivided(location.total?.profit_per_classroom),
          }, // 20
        ]

        const formattedFooterRow = footerRow.map((item, id) =>
          id === 0
            ? item
            : { ...item, ...ExcelRowsParams.footerRowParams.cell },
        )
        tableData.push(formattedFooterRow)
        currentRow++
      })

      let wb = XLSX.utils.book_new()
      const ws = XLSX.utils.aoa_to_sheet([...headRows, ...tableData])
      ws['!cols'] = wscols
      ws['!merges'] = merges
      ws['!rows'] = rows

      XLSX.utils.book_append_sheet(wb, ws, 'Рентабельность программ')
      XLSX.writeFile(wb, 'Рентабельность программ.xlsx')
    })
  }

  const locations = data?.locations || []
  const isFilterEmpty = !filterTextValues.length
  const hasRequiredFilters = filter?.date_from && filter?.date_until

  return (
    <>
      <ContentHeader title='Рентабельность программ' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Search...`}
          value={filter?.search || ''}
          isFilterEmpty={isFilterEmpty}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {isLoading ? (
        <Loader />
      ) : !hasRequiredFilters ? (
        <h1 className={st.noData}>Пожалуйста, выберите период в фильтре</h1>
      ) : data && locations.length > 0 ? (
        <>
          <div style={{ padding: '10px', marginBottom: '10px' }}>
            <ExportExcelButton data={data} downloadExcel={downloadExcel} />
          </div>
          <div className={st.tableWrapper} ref={refWrapper}>
            <div ref={refSticky} className={st.sticky}>
              <table className={st.table}>
                <THead cols={cfg.cols} />
              </table>
            </div>
            <table className={st.table}>
              <THead cols={cfg.cols} ref={refThead} />
              <tbody className={`${st.tbody} ${st.tbodyStriped}`}>
                {/* ВСЕГО ИТОГО - всегда показываем */}
                {data?.total &&
                  (() => {
                    const totalStartPeriod =
                      data.total.students_start_period || 0
                    // Суммируем прибыль по школе для всех локаций
                    const totalProfitBySchool =
                      data.locations?.reduce(
                        (sum, loc) => sum + (loc.total?.profit_by_school || 0),
                        0,
                      ) || 0
                    return (
                      <tr className={st.tdTotalTR}>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          ВСЕГО ИТОГО
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        />
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(totalStartPeriod)}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            data.total.new_students || 0,
                            totalStartPeriod,
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            data.total.left_students || 0,
                            totalStartPeriod,
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatNumberWithPercent(
                            data.total.active_students || 0,
                            totalStartPeriod,
                          )}
                        </td>

                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {calculateCapacity(
                            data.total.hours_worked,
                            data.total.teachers_hours_worked,
                          )}
                        </td>

                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.income_by_hours_worked),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.expense_by_hours_worked),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.profit_by_school),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {calculateProfitabilityPercentage(
                            data.total.profit_by_school || 0,
                            totalProfitBySchool ||
                              data.total.profit_by_school ||
                              0,
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {calculateGrossProfitPercentage(
                            data.total.profit_by_school || 0,
                            data.total.income_by_hours_worked || 0,
                          )}
                        </td>

                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {splitNumberDigits(
                            data.total.hours_worked?.toFixed(2) || '0.00',
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.avg_students_check),
                          )}
                        </td>

                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.income_per_hour_ach),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.income_per_hour),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.expense_per_hour),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.profit_per_hour),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.profit_per_sqm),
                          )}
                        </td>
                        <td
                          className={st.tdTotal}
                          style={{ textAlign: 'left' }}
                        >
                          {formatInteger(
                            divideBy100(data.total.profit_per_classroom),
                          )}
                        </td>
                      </tr>
                    )
                  })()}

                {locations.map((location) => {
                  const locationTotalProfitBySchool =
                    location.program_types.reduce(
                      (sum, program) => sum + (program.profit_by_school || 0),
                      0,
                    )

                  return (
                    <Fragment key={location.id}>
                      {location.program_types.map((pt, index) => {
                        const startPeriod = pt.students_start_period || 0
                        return (
                          <tr
                            key={`${location.id}-${index}`}
                            className={index === 0 ? st.bdt : undefined}
                          >
                            {cfg.cols.map((col, idxCol) => {
                              if (col.group) {
                                if (index === 0)
                                  return (
                                    <td
                                      key={idxCol}
                                      rowSpan={location.program_types.length}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {typeof col.formatter === 'function'
                                        ? col.formatter(location)
                                        : location[col.dataField]}
                                    </td>
                                  )
                                else return null
                              } else {
                                if (col.name === 'Новые студенты') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        pt.new_students || 0,
                                        startPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Выбывшие студенты') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        pt.left_students || 0,
                                        startPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Студенты на конец периода') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {formatNumberWithPercent(
                                        pt.active_students || 0,
                                        startPeriod,
                                      )}
                                    </td>
                                  )
                                }
                                if (
                                  col.name ===
                                  'Наполняемость студентов по отработанным часам'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {calculateCapacity(
                                        pt.hours_worked,
                                        pt.teachers_hours_worked,
                                      )}
                                    </td>
                                  )
                                }
                                if (col.name === 'Рентабельность, %') {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {col.formatter(
                                        pt,
                                        locationTotalProfitBySchool,
                                      )}
                                    </td>
                                  )
                                }
                                if (
                                  col.name === 'Грязная прибыль по школе в % '
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    >
                                      {calculateProfitabilityPercentage(
                                        pt.profit_by_school || 0,
                                        locationTotalProfitBySchool,
                                      )}
                                    </td>
                                  )
                                }
                                // Для колонок "Грязная прибыль с 1 кв м" и "Грязная прибыль с аудитории" - пустые ячейки
                                if (
                                  col.name === 'Грязная прибыль с 1 кв м' ||
                                  col.name === 'Грязная прибыль с аудитории'
                                ) {
                                  return (
                                    <td
                                      key={idxCol}
                                      className={getTdClassNames(col)}
                                      style={{ textAlign: 'left' }}
                                    />
                                  )
                                }
                                return (
                                  <td
                                    key={idxCol}
                                    className={getTdClassNames(col)}
                                    style={{ textAlign: 'left' }}
                                  >
                                    {typeof col.formatter === 'function'
                                      ? col.formatter(pt, startPeriod)
                                      : pt[col.dataField]}
                                  </td>
                                )
                              }
                            })}
                          </tr>
                        )
                      })}
                      <tr>
                        {(() => {
                          const locationStartPeriod =
                            location.total?.students_start_period || 0
                          return (
                            <>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                Итого
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              />
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(locationStartPeriod)}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  location.total?.new_students || 0,
                                  locationStartPeriod,
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  location.total?.left_students || 0,
                                  locationStartPeriod,
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatNumberWithPercent(
                                  location.total?.active_students || 0,
                                  locationStartPeriod,
                                )}
                              </td>

                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {calculateCapacity(
                                  location.total?.hours_worked,
                                  location.total?.teachers_hours_worked,
                                )}
                              </td>

                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(
                                    location.total?.income_by_hours_worked,
                                  ),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(
                                    location.total?.expense_by_hours_worked,
                                  ),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(location.total?.profit_by_school),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {calculateProfitabilityPercentage(
                                  location.total?.profit_by_school || 0,
                                  location.total?.profit_by_school || 0,
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {calculateGrossProfitPercentage(
                                  location.total?.profit_by_school || 0,
                                  location.total?.income_by_hours_worked || 0,
                                )}
                              </td>

                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {splitNumberDigits(
                                  location.total?.hours_worked?.toFixed(2) ||
                                    '0.00',
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(
                                    location.total?.avg_students_check,
                                  ),
                                )}
                              </td>

                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(
                                    location.total?.income_per_hour_ach,
                                  ),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(location.total?.income_per_hour),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(location.total?.expense_per_hour),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(location.total?.profit_per_hour),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(location.total?.profit_per_sqm),
                                )}
                              </td>
                              <td
                                className={st.tdTotal}
                                style={{ textAlign: 'left' }}
                              >
                                {formatInteger(
                                  divideBy100(
                                    location.total?.profit_per_classroom,
                                  ),
                                )}
                              </td>
                            </>
                          )
                        })()}
                      </tr>
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <h1 className={st.noData}>Нет данных для отображения!</h1>
      )}
    </>
  )
}

export default observer(ManagementReport)
